import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
  startAfter,
  getDoc,
  increment,
  writeBatch,
  serverTimestamp
} from '@firebase/firestore';
import { Event, EventType, CauseType  } from '../types/firebase';

const db = getFirestore();
const eventsCollection = collection(db, 'events');
const rsvpsCollection = collection(db, 'rsvps');

// Helper function to validate event data
const validateEventData = (eventData: Partial<Event>): string[] => {
  const errors: string[] = [];
  
  if (!eventData.title?.trim()) errors.push('Title is required');
  if (!eventData.description?.trim()) errors.push('Description is required');
  if (!eventData.eventType) errors.push('Event type is required');
  if (!eventData.cause) errors.push('Cause is required');
  if (!eventData.date) errors.push('Date is required');
  if (!eventData.time?.trim()) errors.push('Time is required');
  if (!eventData.location?.address?.trim()) errors.push('Location address is required');
  if (!eventData.location?.city?.trim()) errors.push('City is required');
  if (!eventData.location?.state?.trim()) errors.push('State is required');
  if (!eventData.location?.country?.trim()) errors.push('Country is required');
  
  return errors;
};

// Create a new event
export const createEvent = async (
  eventData: Partial<Event>,
  userId: string,
  visibility: 'public' | 'private' = 'public'
): Promise<string> => {
  try {
    const errors = validateEventData(eventData);
    if (errors.length > 0) {
      throw new Error(`Validation errors: ${errors.join(', ')}`);
    }

    const now = Timestamp.now();
    const eventTime = eventData.time || now.toDate().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    const newEvent = {
      title: eventData.title!.trim(),
      description: eventData.description!.trim(),
      eventType: eventData.eventType!,
      cause: eventData.cause!,
      date: eventData.date!,
      time: eventTime,
      location: {
        address: eventData.location!.address.trim(),
        city: eventData.location!.city.trim(),
        state: eventData.location!.state.trim(),
        country: eventData.location!.country.trim()
      },
      organizer: {
        uid: userId,
        displayName: eventData.organizer?.displayName || 'Anonymous',
        photoURL: eventData.organizer?.photoURL
      },
      link: eventData.link?.trim(),
      status: 'approved',
      rsvp: {
        going: [],
        maybe: [],
        notGoing: []
      },
      participants: [],
      chatEnabled: false,
      createdAt: now,
      updatedAt: now,
      reportCount: 0,
      featured: false,
      attendanceProcessed: []
    };

    const eventRef = await addDoc(collection(db, 'events'), newEvent);
    return eventRef.id;
  } catch (error) {
    console.error('Error creating event:', error);
    throw error;
  }
};

// Get events with pagination and filtering
export const getEvents = async (
  filters: {
    cause?: CauseType;
    eventType?: EventType;
    fromDate?: Date;
    featured?: boolean;
    status?: Event['status'];
  },
  sort: {
    field: 'date' | 'reportCount' | 'createdAt';
    direction: 'asc' | 'desc';
  } = { field: 'date', direction: 'asc' },
  lastVisible?: any,
  pageSize: number = 10,
  userId?: string
): Promise<{ events: (Event & { __snapshot?: any })[]; lastVisible: any }> => {
  try {
    let q = query(eventsCollection);

    console.log('Starting getEvents with filters:', filters);
    console.log('Sort options:', sort);

    // Apply sorting first
    if (sort.field === 'date') {
      q = query(
        q,
        orderBy('date', sort.direction),
        orderBy('__name__', sort.direction)
      );
    } else if (sort.field === 'reportCount') {
      // For popularity sorting, we'll fetch all matching documents and sort in memory
      const snapshot = await getDocs(q);
      let events = snapshot.docs.map(doc => {
        const data = doc.data() as Event;
        return {
          ...data,
          id: doc.id,
          totalRSVP: (data.rsvp?.going?.length || 0) + (data.rsvp?.maybe?.length || 0),
          __snapshot: doc,
        };
      });
      
      // Sort by total RSVP count
      events.sort((a, b) => b.totalRSVP - a.totalRSVP);
      
      return {
        events: events.slice(0, pageSize),
        lastVisible: snapshot.docs[pageSize - 1] || null
      };
    }

    // Apply filters
    if (filters.eventType) {
      q = query(q, where('eventType', '==', filters.eventType));
    }
    if (filters.cause) {
      q = query(q, where('cause', '==', filters.cause));
    }
    if (filters.fromDate) {
      q = query(q, where('date', '>=', Timestamp.fromDate(filters.fromDate)));
    }
    if (filters.featured !== undefined) {
      q = query(q, where('featured', '==', filters.featured));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    } else {
      // Default to approved events only
      q = query(q, where('status', '==', 'approved'));
    }

    // Build base conditions for both queries
    const baseConditions = [];
    if (filters.eventType) {
      baseConditions.push(where('eventType', '==', filters.eventType));
    }
    if (filters.cause) {
      baseConditions.push(where('cause', '==', filters.cause));
    }
    if (filters.fromDate) {
      baseConditions.push(where('date', '>=', Timestamp.fromDate(filters.fromDate)));
    }
    if (filters.featured !== undefined) {
      baseConditions.push(where('featured', '==', filters.featured));
    }
    if (filters.status) {
      baseConditions.push(where('status', '==', filters.status));
    }

    // Note: eventsQuery was removed as it was not being used

    // Pagination
    q = query(q, limit(pageSize));
    if (lastVisible) {
      q = query(q, startAfter(lastVisible));
    }

    // Execute query
    const snapshot = await getDocs(q);

    // Process results
    const events = snapshot.docs
      .map(doc => {
        const eventData = doc.data() as Event;
        return {
          ...eventData,
          id: doc.id,
          __snapshot: doc,
        } as Event & { __snapshot?: any };
      })
      // Remove groupId filtering, just return all events
      ;
    const lastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    return {
      events,
      lastVisible: lastDoc
    };
  } catch (error) {
    console.error('Error in getEvents:', error);
    throw error;
  }
};

// Get a single event by ID
export const getEvent = async (eventId: string): Promise<Event | null> => {
  const eventRef = doc(eventsCollection, eventId);
  const eventDoc = await getDoc(eventRef);
  
  if (!eventDoc.exists()) {
    return null;
  }
  
  return eventDoc.data() as Event;
};

// Update an event
export const updateEvent = async (eventId: string, userId: string, eventData: Partial<Event>): Promise<void> => {
  try {
    const eventRef = doc(eventsCollection, eventId);
    const eventDoc = await getDoc(eventRef);
    
    if (!eventDoc.exists()) {
      throw new Error('Event not found');
    }

    const event = eventDoc.data() as Event;
    
    // Check if user is authorized to update the event
    if (event.organizer.uid !== userId) {
      // Check if user is admin
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const isAdmin = userDoc.exists() && userDoc.data()?.role === 'admin';
      
      if (!isAdmin) {
        console.error('Unauthorized: Event organizer:', event.organizer.uid, 'Current user:', userId);
        throw new Error('Unauthorized to update this event');
      }
    }

    // Only update specific fields that we allow to be updated
    const updateData = {
      title: eventData.title,
      description: eventData.description,
      eventType: eventData.eventType,
      cause: eventData.cause,
      date: eventData.date,
      time: eventData.time,
      location: eventData.location,
      link: eventData.link,
      updatedAt: Timestamp.now(),
    };

    // Remove undefined values
    const cleanedUpdateData = Object.entries(updateData)
      .reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);

    await updateDoc(eventRef, cleanedUpdateData);
  } catch (error) {
    console.error('Error in updateEvent:', error);
    throw error;
  }
};

// Handle RSVP
export const updateRSVP = async (
  eventId: string,
  userId: string,
  status: 'going' | 'maybe' | 'notGoing' | null
): Promise<void> => {
  const eventRef = doc(eventsCollection, eventId);
  const userRef = doc(db, 'users', userId);
  const batch = writeBatch(db);
  
  try {
    const [eventDoc] = await Promise.all([
      getDoc(eventRef),
      getDoc(userRef) // userDoc is fetched but not used in this function
    ]);
    
    if (!eventDoc.exists()) {
      throw new Error('Event not found');
    }

    const event = eventDoc.data() as Event;
    
    // Store original state before any modifications
    const wasGoingBefore = event.rsvp?.going?.includes(userId) || false;
    
    // Create update object with default empty arrays
    const updateData: any = {
      rsvp: {
        going: [],
        maybe: [],
        notGoing: []
      },
      participants: event.participants || [],
      updatedAt: Timestamp.now()
    };

    // Copy existing RSVP lists if they exist
    if (event.rsvp?.going) updateData.rsvp.going = [...event.rsvp.going];
    if (event.rsvp?.maybe) updateData.rsvp.maybe = [...event.rsvp.maybe];
    if (event.rsvp?.notGoing) updateData.rsvp.notGoing = [...event.rsvp.notGoing];
    
    // Remove user from all RSVP lists
    updateData.rsvp.going = updateData.rsvp.going.filter((id: string) => id !== userId);
    updateData.rsvp.maybe = updateData.rsvp.maybe.filter((id: string) => id !== userId);
    updateData.rsvp.notGoing = updateData.rsvp.notGoing.filter((id: string) => id !== userId);
    
    // Add user to selected RSVP list if status is not null
    if (status) {
      updateData.rsvp[status].push(userId);
    }
    
    // Update participants list
    updateData.participants = updateData.participants.filter((id: string) => id !== userId);
    if (status === 'going') {
      updateData.participants.push(userId);
    }

    // Update event document
    batch.update(eventRef, updateData);

    // Update user's stats based on the change in RSVP status
    const isGoingNow = status === 'going';
    
    if (!wasGoingBefore && isGoingNow) {
      // User is now going but wasn't before
      batch.update(userRef, {
        lastActive: Timestamp.now()
      });
    } else if (wasGoingBefore && !isGoingNow) {
      // User was going but isn't anymore
      batch.update(userRef, {
        lastActive: Timestamp.now()
      });
    }

    // Update or create RSVP document
    const rsvpRef = doc(rsvpsCollection, `${eventId}_${userId}`);
    if (status) {
      batch.set(rsvpRef, {
        eventId,
        userId,
        status,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    } else {
      batch.delete(rsvpRef);
    }

    await batch.commit();
  } catch (error) {
    console.error('Error updating RSVP:', error);
    throw error;
  }
};

// Report an event
export const reportEvent = async (eventId: string): Promise<void> => {
  const eventRef = doc(eventsCollection, eventId);
  const eventDoc = await getDoc(eventRef);
  
  if (!eventDoc.exists()) {
    throw new Error('Event not found');
  }

  await updateDoc(eventRef, {
    reportCount: (eventDoc.data() as Event).reportCount + 1,
    updatedAt: serverTimestamp()
  });
};

// Delete an event
export const deleteEvent = async (eventId: string, userId: string): Promise<void> => {
  try {
    const eventRef = doc(eventsCollection, eventId);
    const eventDoc = await getDoc(eventRef);
    
    if (!eventDoc.exists()) {
      throw new Error('Event not found');
    }

    const event = eventDoc.data() as Event;
    
    // Check if user is authorized to delete the event
    if (event.organizer.uid !== userId) {
      // Check if user is admin
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const isAdmin = userDoc.exists() && userDoc.data()?.role === 'admin';
      
      if (!isAdmin) {
        console.error('Unauthorized: Event organizer:', event.organizer.uid, 'Current user:', userId);
        throw new Error('Unauthorized to delete this event');
      }
    }
    
    // Delete all related data
    const batch = writeBatch(db);

    // 1. Delete all comments for this event (from comments collection)
    const commentsQuery = query(collection(db, 'comments'), where('eventId', '==', eventId));
    const commentsSnapshot = await getDocs(commentsQuery);
    commentsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // 2. Delete all RSVPs for this event
    const rsvpsQuery = query(collection(db, 'rsvps'), where('eventId', '==', eventId));
    const rsvpsSnapshot = await getDocs(rsvpsQuery);
    rsvpsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // 3. Delete all notifications related to this event
    const notificationsQuery = query(collection(db, 'notifications'), where('eventId', '==', eventId));
    const notificationsSnapshot = await getDocs(notificationsQuery);
    notificationsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // 4. Delete all reports related to this event
    const reportsQuery = query(collection(db, 'reports'), where('contentId', '==', eventId), where('contentType', '==', 'event'));
    const reportsSnapshot = await getDocs(reportsQuery);
    reportsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // 5. Delete any event reactions (if they exist in a separate collection)
    const eventReactionsQuery = query(collection(db, 'event_reactions'), where('eventId', '==', eventId));
    const eventReactionsSnapshot = await getDocs(eventReactionsQuery);
    eventReactionsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // 6. Delete any event attachments/files (if they exist)
    const eventFilesQuery = query(collection(db, 'event_files'), where('eventId', '==', eventId));
    const eventFilesSnapshot = await getDocs(eventFilesQuery);
    eventFilesSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // 7. Delete the event document itself
    batch.delete(eventRef);

    // 8. Update user's stats
    const userRef = doc(db, 'users', userId);
    batch.update(userRef, {
      lastActive: Timestamp.now()
    });
    
    // Only decrement events created counter for future/ongoing events
    const eventDate = event.date.toDate();
    const now = new Date();
    if (eventDate > now) {
      batch.update(userRef, {
        'stats.eventsCreated': increment(-1)
      });
    }

    // Commit all the changes
    await batch.commit();

    console.log('Successfully deleted event and all related data');
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

// Get event by ID
export const getEventById = async (eventId: string): Promise<Event> => {
  const eventDoc = await getDoc(doc(eventsCollection, eventId));
  
  if (!eventDoc.exists()) {
    throw new Error('Event not found');
  }
  
  return eventDoc.data() as Event;
};

// Add a new function to recalculate events created count
export const recalculateEventsCreated = async (userId: string): Promise<void> => {
  try {
    const now = new Date();
    
    // Get all events created by the user
    const q = query(
      eventsCollection,
      where('organizer.uid', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    let activeAndPastEventsCount = 0;
    
    querySnapshot.forEach((doc) => {
      const event = doc.data() as Event;
      const eventDate = event.date.toDate();
      // Count events that are either upcoming or have already occurred
      if (eventDate >= now || eventDate < now) {
        activeAndPastEventsCount++;
      }
    });
    
    // Update the user's stats with the accurate count
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'stats.eventsCreated': activeAndPastEventsCount,
      lastActive: Timestamp.now()
    });
  } catch (error) {
    console.error('Error recalculating events created:', error);
    throw error;
  }
};

// Add a new function to check and update event attendance
export const checkAndUpdateEventAttendance = async (userId: string): Promise<void> => {
  try {
    const now = Timestamp.now();
    const eventsQuery = query(
      collection(db, 'events'),
      where('date', '<=', now),
      where('status', '==', 'approved')
    );
    const eventsSnapshot = await getDocs(eventsQuery);

    const batch = writeBatch(db);
    let eventsAttended = 0;

    for (const eventDoc of eventsSnapshot.docs) {
      const event = eventDoc.data() as Event;
      
      // Check if user was going to this event and hasn't been processed yet
      if (event.rsvp.going.includes(userId) && 
          !event.attendanceProcessed?.includes(userId)) {
        
        // Mark user as attended
        batch.update(eventDoc.ref, {
          attendanceProcessed: [...(event.attendanceProcessed || []), userId]
        });
        
        eventsAttended++;
      }
    }

    if (eventsAttended > 0) {
      const userRef = doc(db, 'users', userId);
      batch.update(userRef, {
        'stats.eventsAttended': increment(eventsAttended),
        updatedAt: Timestamp.now()
      });
    }

    await batch.commit();
  } catch (error) {
    console.error('Error checking and updating event attendance:', error);
    throw error;
  }
}; 